import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { NavBarComponent } from '../../../shared/components/nav-bar/nav-bar.component';
import { ApiService } from '../../../services/api.service';

interface ActivityItem {
  marketIcon: string;
  marketName: string;
  marketWindow: string;
  traderAvatar: string;
  traderName: string;
  action: string;
  actionColor: string;
  price: string;
  amount: string;
  timeAgo: string;
  txn_hash?: string;
}

@Component({
  selector: 'app-activity',
  imports: [CommonModule, NavBarComponent],
  templateUrl: './activity.component.html',
  styleUrl: './activity.component.css'
})
export class ActivityComponent implements OnInit {
  activities: ActivityItem[] = [];
  isLoading = false;
  marketList: any[] = [];
  currentPage = 1;
  itemsPerPage = 25;
  totalItems = 0;

  constructor(private api: ApiService) {}

  ngOnInit(): void {
    console.log('🚀 ActivityComponent initializing...');
    this.isLoading = true;
    this.loadMarkets();
  }

  loadMarkets(): void {
    // Fetch markets with empty filters
    console.log('Fetching markets list...');
    this.api.getMarketsList({ category_ids: [], tag_ids: [] }).subscribe({
      next: (res) => {
        console.log('Markets loaded:', res.data?.results?.length || 0, 'markets');
        this.marketList = res.data?.results || [];
        console.log('First few market IDs:', this.marketList.slice(0, 3).map((m: any) => m.id));
        this.loadActivityData();
      },
      error: (err) => {
        console.error('Failed to load markets:', err);
        this.isLoading = false;
      }
    });
  }

  loadActivityData(): void {
    if (!this.marketList || this.marketList.length === 0) {
      console.warn('No markets available');
      this.activities = [];
      this.isLoading = false;
      return;
    }

    // Limit to first 20 markets to avoid too many parallel requests
    const marketsToLoad = this.marketList.slice(0, 20);
    console.log(`Loading activity for ${marketsToLoad.length} markets (out of ${this.marketList.length} total)`);
    this.isLoading = true;
    
    // Create promises for each market's activity
    const activityPromises = marketsToLoad.map((market: any) => {
      console.log(`Requesting activity for market: ${market.id} (${market.name})`);
      return this.api.getMarketActivity(market.id, null, 1, 50).toPromise()
        .then((res: any) => {
          const count = res.data?.results?.length || 0;
          console.log(`✓ Activity loaded for market ${market.id}: ${count} items`);
          return { data: res, market };
        })
        .catch((err: any) => {
          console.error(`✗ Error loading activity for market ${market.id}:`, err.status, err.statusText, err.error?.detail);
          return { data: null, market };
        })
    });

    Promise.all(activityPromises).then((results) => {
      console.log('All activity promises resolved');
      const allActivities: ActivityItem[] = [];

      results.forEach((result: any) => {
        if (result.data?.data?.results && Array.isArray(result.data.data.results)) {
          result.data.data.results.forEach((item: any) => {
            const activity = this.mapApiActivityToItem(item, result.market);
            if (activity) {
              allActivities.push(activity);
            }
          });
        }
      });

      this.totalItems = allActivities.length;
      this.activities = allActivities;
      this.currentPage = 1;
      this.isLoading = false;
      console.log('✓ Activity component loaded with', this.activities.length, 'total items');
    }).catch((err) => {
      console.error('✗ Error in Promise.all:', err);
      this.isLoading = false;
    });
  }

  mapApiActivityToItem(apiItem: any, market: any): ActivityItem | null {
    try {
      const isBuy = apiItem.activity_text?.toLowerCase().includes('bought') || 
                    apiItem.activity_text?.toLowerCase().includes('buy');
      const actionColor = isBuy ? 'text-green-600' : 'text-red-500';
      
      // Extract amount and price from activity text or use defaults
      const parts = apiItem.activity_text?.split(' ') || [];
      const amount = parts[2] || 'N/A';
      const price = apiItem.activity_text?.match(/\$[\d.]+/) ? 
        apiItem.activity_text.match(/\$[\d.]+/)[0] : '$0.00';

      return {
        marketIcon: market.image_url || 'assets/images/deposit/btc.svg',
        marketName: market.name || apiItem.market_name || 'Market',
        marketWindow: new Date().toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        traderAvatar: apiItem.profile_picture || 'assets/images/home/profile-image.png',
        traderName: apiItem.activity_text?.split(' ')[0] || 'Trader',
        action: isBuy ? 'Yes' : 'No',
        actionColor,
        price,
        amount: `$${amount}`,
        timeAgo: apiItem.time_diff || 'Just now',
        txn_hash: apiItem.txn_hash
      };
    } catch (e) {
      console.error('Error mapping activity item:', e);
      return null;
    }
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  get paginatedActivities(): ActivityItem[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.activities.slice(startIndex, endIndex);
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems / this.itemsPerPage);
  }

  get pageNumbers(): number[] {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages; i++) {
      pages.push(i);
    }
    return pages;
  }
}
